import { config } from '@/forms/config';
import type {
  FormConditionalValidation,
  FormField,
  FormInitialValues,
  FormValidations,
} from '@/forms/typings';
import type { FieldsGroupFormField } from '@/forms/typings';
import {
  addressFieldsShownFieldId,
  addressLinkFieldId,
  getAddressChoiceState,
} from '@/forms/utils/addressLink';
import { createAddressLinkYupSchema } from '@/forms/utils/createAddressLinkYup';
import { registerGroupRestrictionValidations } from '@/forms/utils/createAtLeastOneChildWithValueYup';

import type { FormFieldModuleContext } from '../registry/field-module.types';

export function buildFieldsGroupInitialState(
  field: FormField,
  values: FormInitialValues,
  validations: FormValidations,
  conditions: FormConditionalValidation[] | undefined,
  ctx: FormFieldModuleContext,
): { initialValues: FormInitialValues; validationFields: FormValidations } {
  let initialValues: FormInitialValues = { ...values };
  let validationFields: FormValidations = { ...validations };

  const f = field as FieldsGroupFormField;

  if (f.notApplicableField) {
    initialValues[f.notApplicableField.fieldId] = f.notApplicableField.value;
  }

  const choice = getAddressChoiceState(f.address, f.address?.linkValue);

  const shouldValidateFieldId = `${f.fieldId}${config.shouldValidateFieldSuffix}`;

  initialValues[shouldValidateFieldId] =
    !f.notApplicable && !choice.hidesAllFields;

  const combinedConditions: FormConditionalValidation[] = [
    {
      type: 'should-validate',
      relatedFieldId: shouldValidateFieldId,
    },
  ];

  if (conditions) {
    combinedConditions.push(...conditions);
  }

  // Seeded to match what AddressSourceChoice and FieldsGroupPresenter write.
  const hiddenConditions: FormConditionalValidation[] = [
    ...combinedConditions,
    {
      type: 'should-validate',
      relatedFieldId: addressFieldsShownFieldId(f.fieldId),
    },
  ];

  if (choice.showsChoice) {
    const linkFieldId = addressLinkFieldId(f.fieldId);

    initialValues[linkFieldId] = f.address?.linkValue;
    initialValues[addressFieldsShownFieldId(f.fieldId)] = !choice.isLinked;

    // Gated by the parent only: this group's own gate is off until it is answered.
    if (f.address?.answerRequired) {
      validationFields[linkFieldId] = createAddressLinkYupSchema(conditions);
    }
  }

  registerGroupRestrictionValidations(
    initialValues,
    validationFields,
    f.fieldId,
    f.fields,
    f.restriction,
    combinedConditions,
  );

  const linkHides = new Set(
    choice.showsChoice ? f.address?.hiddenFieldIds : [],
  );

  for (const inner of f.fields) {
    const detailValuesAndValidations = ctx.recurse(
      inner,
      initialValues,
      validationFields,
      linkHides.has(inner.fieldId) ? hiddenConditions : combinedConditions,
    );

    initialValues = {
      ...initialValues,
      ...detailValuesAndValidations.initialValues,
    };
    validationFields = {
      ...validationFields,
      ...detailValuesAndValidations.validationFields,
    };
  }

  if (f.naDetails && f.notApplicableField) {
    const combinedNAConditions: FormConditionalValidation[] = [
      { type: 'not-applicable', relatedFieldId: f.notApplicableField.fieldId },
      ...(conditions ?? []),
    ];

    const naValuesAndValidations = ctx.recurse(
      f.naDetails,
      initialValues,
      validationFields,
      combinedNAConditions,
    );

    initialValues = {
      ...initialValues,
      ...naValuesAndValidations.initialValues,
    };
    validationFields = {
      ...validationFields,
      ...naValuesAndValidations.validationFields,
    };
  }

  return { initialValues, validationFields };
}
